import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../styles/Header.scss';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { getLocalizedPath } from '../i18n';

const Header = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useParams();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const langDropdownRef = useRef(null);
  const menuRef = useRef(null);
  
  const languages = [
    { code: 'uk', name: 'UA', flag: '/assets/flags/ukraine.svg' },
    { code: 'en', name: 'EN', flag: '/assets/flags/uk.svg' },
    { code: 'de', name: 'DE', flag: '/assets/flags/germany.svg' },
    { code: 'lt', name: 'LT', flag: '/assets/flags/lithuania.svg' },
  ];
  
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
        setLangDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Check if device is mobile
  useEffect(() => {
    // Initialize isMobile state
    setIsMobile(window.innerWidth <= 1024);
    
    const handleResize = () => {
      const mobile = window.innerWidth <= 1024;
      setIsMobile(mobile);
      
      if (!mobile && menuOpen) {
        setMenuOpen(false);
        document.body.classList.remove('menu-open');
        document.body.style.overflow = '';
      }
    };
    
    // Call once to initialize
    handleResize();
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [menuOpen]);
  
  // Handle body overflow when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.classList.add('menu-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('menu-open');
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.classList.remove('menu-open');
      document.body.style.overflow = '';
    };
  }, [menuOpen]);
  
  // Close menu when route changes
  useEffect(() => {
    setMenuOpen(false);
    document.body.classList.remove('menu-open');
    document.body.style.overflow = '';
  }, [location.pathname]);
  
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const toggleLangDropdown = () => {
    setLangDropdownOpen(!langDropdownOpen);
  };

  const changeLanguage = (lng) => {
    // Визначаємо поточний шлях без мови
    const currentPath = location.pathname;
    // Отримуємо новий локалізований шлях
    const newPath = getLocalizedPath(currentPath, lng);
    
    // Змінюємо мову та перенаправляємо на новий URL
    i18n.changeLanguage(lng);
    navigate(newPath);
    
    setLangDropdownOpen(false);
  };

  const getCurrentLanguage = () => {
    return languages.find(lang => lang.code === language) || languages[0];
  };

  // Функція для створення локалізованого посилання
  const getLocalizedLink = (path) => {
    return `/${language}${path === '/' ? '' : path}`;
  };

  return (
    <header className={`header ${scrolled ? 'scrolled' : ''} ${isMobile ? 'mobile' : ''} ${menuOpen ? 'menu-open' : ''}`}>
      <div className="container">
        <div className="header-content">
          <button 
            className="menu-toggle" 
            onClick={toggleMenu} 
            aria-label="Toggle menu"
          >
            <span className="bar"></span>
            <span className="bar"></span>
            <span className="bar"></span>
          </button>
          
          <div className="logo">
            <Link to={getLocalizedLink('/')}>
              <img src="/assets/pic/ai-psy-help-logo-f1.png" alt="AI PSY HELP" />
            </Link>
          </div>
          
          {!isMobile ? (
            <nav className="main-nav">
              <ul className="nav-links">
                <li><Link to={getLocalizedLink('/')} className={location.pathname === `/${language}` ? 'active' : ''}>{t('nav.home')}</Link></li>
                <li><Link to={getLocalizedLink('/news')} className={location.pathname === `/${language}/news` ? 'active' : ''}>{t('nav.news')}</Link></li>
                <li><Link to={getLocalizedLink('/videos')} className={location.pathname === `/${language}/videos` ? 'active' : ''}>{t('nav.videos')}</Link></li>
                <li className={i18n.language === 'de' || i18n.language === 'lt' ? 'long-text' : ''}>
                  <Link to={getLocalizedLink('/corporate')} className={location.pathname === `/${language}/corporate` ? 'active' : ''}>{t('nav.corporate')}</Link>
                </li>
                <li className={i18n.language === 'de' || i18n.language === 'lt' ? 'long-text' : ''}>
                  <Link to={getLocalizedLink('/ngo')} className={location.pathname === `/${language}/ngo` ? 'active' : ''}>{t('nav.ngo')}</Link>
                </li>
                <li><Link to={getLocalizedLink('/webinars')} className={location.pathname === `/${language}/webinars` ? 'active' : ''}>{t('nav.webinars')}</Link></li>
              </ul>
            </nav>
          ) : null}
          
          <div className={`mobile-menu ${menuOpen ? 'open' : ''}`} ref={menuRef}>
            <div className="mobile-menu-content">
              <ul className="mobile-nav-links">
                <li><Link to={getLocalizedLink('/')} className={location.pathname === `/${language}` ? 'active' : ''}>{t('nav.home')}</Link></li>
                <li><Link to={getLocalizedLink('/news')} className={location.pathname === `/${language}/news` ? 'active' : ''}>{t('nav.news')}</Link></li>
                <li><Link to={getLocalizedLink('/videos')} className={location.pathname === `/${language}/videos` ? 'active' : ''}>{t('nav.videos')}</Link></li>
                <li><Link to={getLocalizedLink('/corporate')} className={location.pathname === `/${language}/corporate` ? 'active' : ''}>{t('nav.corporate')}</Link></li>
                <li><Link to={getLocalizedLink('/ngo')} className={location.pathname === `/${language}/ngo` ? 'active' : ''}>{t('nav.ngo')}</Link></li>
                <li><Link to={getLocalizedLink('/webinars')} className={location.pathname === `/${language}/webinars` ? 'active' : ''}>{t('nav.webinars')}</Link></li>
              </ul>
              
              <div className="mobile-menu-extra">
                <div className="mobile-languages">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      className={`mobile-lang-option ${language === lang.code ? 'active' : ''}`}
                      onClick={() => changeLanguage(lang.code)}
                    >
                      <img src={lang.flag} alt={lang.name} className="flag-icon" />
                    </button>
                  ))}
                </div>
                
                <a href="https://chat.psyhelp.info/" target="_blank" rel="noopener noreferrer" className="mobile-login-btn">{t('nav.login')}</a>
              </div>
            </div>
          </div>
          
          {!isMobile && (
            <div className="header-actions">
              <div className="lang-switcher" ref={langDropdownRef}>
                <button 
                  className="lang-current" 
                  onClick={toggleLangDropdown}
                  aria-label={`Change language from ${getCurrentLanguage().name}`}
                >
                  <div className="lang-current-content">
                    <img 
                      src={getCurrentLanguage().flag} 
                      alt={getCurrentLanguage().name} 
                      className="flag-icon"
                    />
                    <span>{getCurrentLanguage().name}</span>
                  </div>
                  <div className="dropdown-icon-wrapper">
                    {langDropdownOpen ? <FaChevronUp className="dropdown-icon" /> : <FaChevronDown className="dropdown-icon" />}
                  </div>
                </button>
                
                {langDropdownOpen && (
                  <div className="lang-dropdown">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        className={`lang-option ${language === lang.code ? 'active' : ''}`}
                        onClick={() => changeLanguage(lang.code)}
                      >
                        <div className="lang-option-content">
                          <img src={lang.flag} alt={lang.name} className="flag-icon" />
                          <span>{lang.name}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="auth-buttons">
                <a href="https://chat.psyhelp.info/" target="_blank" rel="noopener noreferrer" className="btn btn-ghost nav-sign-in">{t('nav.login')}</a>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header; 